import { useApiClient } from '@startupquickstart/frontend';
import { useCallback, useState } from 'react';

interface PresignResponse {
  data: { id: string; uploadUrl: string; url: string };
}

interface UseVideoUploadResult {
  /** Presign + upload a video straight to S3; resolves to the created media id. */
  uploadVideo: (file: File) => Promise<string>;
  isUploading: boolean;
  /** 0–100 upload progress. */
  progress: number;
}

/**
 * PUT a file to a presigned S3 URL, reporting progress via XHR.
 */
function putToS3(
  uploadUrl: string,
  file: File,
  contentType: string,
  onProgress: (pct: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`Upload failed (${xhr.status})`));
    xhr.onerror = () => reject(new Error('Upload failed'));
    xhr.send(file);
  });
}

/**
 * Uploads a (potentially large) video directly to S3 via a presigned URL — the
 * file never passes through our API. Returns the Media id, which is served by
 * the existing media pipeline.
 */
export function useVideoUpload(): UseVideoUploadResult {
  const apiClient = useApiClient();
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const uploadVideo = useCallback(
    async (file: File): Promise<string> => {
      setIsUploading(true);
      setProgress(0);
      try {
        const presign = (await apiClient.post('/media/presign-video', {
          fileName: file.name,
          contentType: file.type,
        })) as PresignResponse;

        const { id, uploadUrl } = presign.data;
        await putToS3(uploadUrl, file, file.type, setProgress);
        setProgress(100);
        return id;
      } finally {
        setIsUploading(false);
      }
    },
    [apiClient],
  );

  return { uploadVideo, isUploading, progress };
}
